import { useEffect, useState } from 'react';
import Modal from './ui/Modal.jsx';
import { pb } from '../lib/pocketbase.js';
import {
  PRAXIS_VERTRAG_PARTNER_OPTIONS,
  PRAXIS_VERTRAG_STATUS_OPTIONS,
} from '../lib/praxis.js';

// Vertragsfelder werden im PB als optional / null abgelegt. Im Form-State
// halten wir Strings ('' = nicht erfasst), damit Inputs controlled bleiben.
const EMPTY = {
  vertrag_partner: '',
  vertrag_status: '',
  vertrag_kundennummer: '',
  vertrag_versendet_am: '',
  vertrag_beginn_am: '',
  vertrag_ende_am: '',
  vertrag_medifin_gebuehr_prozent: '',
  vertrag_factoring_gebuehr_prozent: '',
  vertrag_grundgebuehr_eur: '',
};

function fromPraxis(praxis) {
  if (!praxis) return EMPTY;
  const out = { ...EMPTY };
  for (const key of Object.keys(EMPTY)) {
    const v = praxis[key];
    if (v === null || v === undefined) continue;
    out[key] = String(v);
  }
  return out;
}

function numberOrNull(s) {
  if (s === '' || s === null || s === undefined) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export default function VertragFormModal({ open, onClose, onSaved, praxis }) {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(fromPraxis(praxis));
      setError(null);
    }
  }, [open, praxis]);

  function handleChange(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    // Validierung: Partner ohne Status wäre inkonsistent — Sales würde nicht
    // wissen, wo der Vertrag im Prozess steht. Andere Felder bleiben
    // optional, auch wenn Partner gesetzt ist.
    if (form.vertrag_partner && !form.vertrag_status) {
      setError('Wenn ein Partner gewählt ist, muss auch ein Vertragsstatus gesetzt sein.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        vertrag_partner: form.vertrag_partner || null,
        vertrag_status: form.vertrag_status || null,
        vertrag_kundennummer: form.vertrag_kundennummer.trim(),
        vertrag_versendet_am: form.vertrag_versendet_am || null,
        vertrag_beginn_am: form.vertrag_beginn_am || null,
        vertrag_ende_am: form.vertrag_ende_am || null,
        vertrag_medifin_gebuehr_prozent: numberOrNull(form.vertrag_medifin_gebuehr_prozent),
        vertrag_factoring_gebuehr_prozent: numberOrNull(form.vertrag_factoring_gebuehr_prozent),
        vertrag_grundgebuehr_eur: numberOrNull(form.vertrag_grundgebuehr_eur),
      };

      const saved = await pb.collection('praxen').update(praxis.id, payload);
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(err?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Factoring-Vertrag" size="lg">
      <form onSubmit={handleSubmit} className="space-y-1">
        <SectionHeader>Vertrag</SectionHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="label">Partner</label>
            <select
              className="input mt-1"
              value={form.vertrag_partner}
              onChange={handleChange('vertrag_partner')}
            >
              <option value="">— nicht erfasst —</option>
              {PRAXIS_VERTRAG_PARTNER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Status</label>
            <select
              className="input mt-1"
              value={form.vertrag_status}
              onChange={handleChange('vertrag_status')}
            >
              <option value="">— nicht erfasst —</option>
              {PRAXIS_VERTRAG_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Kundennummer beim Partner</label>
            <input
              type="text"
              className="input mt-1"
              placeholder="z. B. AS-12345"
              value={form.vertrag_kundennummer}
              onChange={handleChange('vertrag_kundennummer')}
            />
            <p className="mt-1 text-xs text-slate-500">
              Für späteres API-Matching. Format ist vom Partner abhängig.
            </p>
          </div>
        </div>

        <SectionHeader>Zeitraum</SectionHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="label">Versendet am</label>
            <input
              type="date"
              className="input mt-1"
              value={form.vertrag_versendet_am}
              onChange={handleChange('vertrag_versendet_am')}
            />
          </div>
          <div>
            <label className="label">Beginn am</label>
            <input
              type="date"
              className="input mt-1"
              value={form.vertrag_beginn_am}
              onChange={handleChange('vertrag_beginn_am')}
            />
          </div>
          <div>
            <label className="label">Ende am</label>
            <input
              type="date"
              className="input mt-1"
              value={form.vertrag_ende_am}
              onChange={handleChange('vertrag_ende_am')}
            />
          </div>
        </div>

        <SectionHeader>Konditionen</SectionHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="label">medifin-Gebühr</label>
            <div className="relative mt-1">
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                className="input pr-8"
                placeholder="z. B. 2.50"
                value={form.vertrag_medifin_gebuehr_prozent}
                onChange={handleChange('vertrag_medifin_gebuehr_prozent')}
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">
                %
              </span>
            </div>
          </div>
          <div>
            <label className="label">Factoring-Gebühr</label>
            <div className="relative mt-1">
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                className="input pr-8"
                placeholder="z. B. 1.80"
                value={form.vertrag_factoring_gebuehr_prozent}
                onChange={handleChange('vertrag_factoring_gebuehr_prozent')}
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">
                %
              </span>
            </div>
          </div>
          <div>
            <label className="label">Grundgebühr / Rechnung</label>
            <div className="relative mt-1">
              <input
                type="number"
                min="0"
                max="999.99"
                step="0.01"
                className="input pr-8"
                placeholder="z. B. 0.50"
                value={form.vertrag_grundgebuehr_eur}
                onChange={handleChange('vertrag_grundgebuehr_eur')}
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">
                €
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
            {error}
          </div>
        )}

        <div className="sticky bottom-0 -mx-6 -mb-5 mt-6 flex justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Speichern …' : 'Speichern'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SectionHeader({ children }) {
  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {children}
      </div>
    </div>
  );
}
